import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { trackAddToCart } from "../lib/analytics";

const StoreContext = createContext(null);

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => loadJSON("nalise_cart", []));
  const [wishlist, setWishlist] = useState(() => loadJSON("nalise_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("nalise_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("nalise_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((it) => it.id === product.id);
      if (existing) {
        return prev.map((it) =>
          it.id === product.id ? { ...it, quantity: it.quantity + quantity } : it
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: parseFloat(product.price || product.regular_price || 0),
          image: product.images?.[0]?.src,
          slug: product.slug,
          quantity,
        },
      ];
    });
    setCartOpen(true);
    trackAddToCart(product, quantity);
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((it) => it.id !== id));

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setCart((prev) => prev.map((it) => (it.id === id ? { ...it, quantity: qty } : it)));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((i) => i !== productId) : [...prev, productId]
    );
  };

  const inWishlist = (productId) => wishlist.includes(productId);

  const subtotal = useMemo(
    () => cart.reduce((sum, it) => sum + (it.price || 0) * it.quantity, 0),
    [cart]
  );
  const itemCount = useMemo(
    () => cart.reduce((sum, it) => sum + it.quantity, 0),
    [cart]
  );

  return (
    <StoreContext.Provider
      value={{
        cart,
        wishlist,
        cartOpen,
        setCartOpen,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        toggleWishlist,
        inWishlist,
        subtotal,
        itemCount,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
